import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'
import {
  Button,
  Card,
  Loading as LoadingButton
} from '@tecsinapse/cortex-react'
import clsx from 'clsx'
import React from 'react'
import { Roles } from '@/core/enums/roles'
import { AccessControl } from '@/core/utils/access-control'
import { OptionSelect } from '@/core/utils/types'
import { UsuarioDialogByGruposUsuarios } from '@/core/components/usuario-dialog-by-grupos-usuarios'
import { AccordionCargosFuncionalidades } from '../components/cargos-funcionalidades'
import { AccordionGrupos } from '../components/grupos'
import { AccordionSetores } from '../components/setores'
import { AccordionUsuariosManuais } from '../components/usuarios-manuais'
import { PodeCriarGrupoPublico } from '../components/pode-criar-grupo-publico'
import { useGrupoUsuariosEdicao } from '../hooks/use-edicao'
import { isUsuarioDialogEnabled } from '../utils/functions'
import { useGrupoUsuariosEdicaoForm } from '../hooks/use-form'
import { AccordionPerfis } from '../components/perfis'
import { FormTextField } from '../../hook-form-connectors/form-text-field'
import { FormProvider } from 'react-hook-form'
import { createPortal } from 'react-dom'
import { Loading } from '@/core/components/loading'
import { AccordionDepartamentos } from '../components/departamentos'

export type GrupoUsuariosEdicaoProps = {
  handleCloseDialog?: (grupocriado: OptionSelect) => void
  onClose?: () => void
  onOpen?: () => void
  isModal?: boolean
}

export const GrupoUsuariosEdicao = ({
  isModal = false,
  handleCloseDialog,
  onClose,
  onOpen
}: GrupoUsuariosEdicaoProps) => {
  const { form, loadingGrupo, submitGroup, values } =
    useGrupoUsuariosEdicaoForm({
      handleCloseDialog,
      onClose,
      onOpen,
      isModal
    })

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    control
  } = form

  const {
    isVisualizarDestinatariosOpen,
    handleOpenVisualizarDestinatarios,
    handleCloseVisualizarDestinatarios,
    possuiUsuariosManuais,
    possuiPerfil,
    getOptions,
    tiposUsuariosSelecionados,
    possuiCargos,
    possuiSetores,
    possuiGrupos,
    possuiDepartamentos
  } = useGrupoUsuariosEdicao({
    control
  })

  if (loadingGrupo) return <Loading />

  return (
    <FormProvider {...form}>
      <div
        className={clsx(
          { 'overflow-hidden': isVisualizarDestinatariosOpen },
          'w-full flex flex-col gap-deca'
        )}
      >
        <p className={'font-bold text-h1 w-full'}>
          {values?.id ? values?.nome : <Trans>Novo Grupo de Usuários</Trans>}
        </p>
        <Card className='flex flex-row gap-x-deca'>
          <div className={'sm:w-[50%]'}>
            <p className={'font-bold mb-mili'}>
              <Trans>Nome do novo Grupo de Usuários:</Trans>
            </p>
            <FormTextField label={t`Nome`} name='nome' />
          </div>
          <AccessControl
            allowedRoles={[Roles.PORTAL_CRIACAO_GRUPOS_USUARIOS_PUBLICOS]}
          >
            <PodeCriarGrupoPublico />
          </AccessControl>
        </Card>
        <Card className='flex flex-col gap-y-mili'>
          <div
            className={
              'flex flex-row space-x-deca justify-between items-center'
            }
          >
            <p className={'font-bold text-h3'}>
              <Trans>Criação do grupo:</Trans>
            </p>
            <Button
              type={'button'}
              variants={{ variant: 'text', className: 'px-1' }}
              onClick={handleOpenVisualizarDestinatarios}
              disabled={
                !isUsuarioDialogEnabled(
                  possuiUsuariosManuais,
                  possuiPerfil,
                  possuiCargos,
                  possuiSetores,
                  possuiGrupos,
                  errors
                )
              }
            >
              <Trans>Ver Destinatários</Trans>
            </Button>
          </div>
          <AccordionPerfis
            possuiPerfil={possuiPerfil}
            options={getOptions}
            tiposUsuariosSelecionados={tiposUsuariosSelecionados}
          />
          <AccordionCargosFuncionalidades possuiCargos={possuiCargos} />
          <AccordionSetores possuiSetores={possuiSetores} />
          <AccordionDepartamentos possuiDepartamentos={possuiDepartamentos} />
          <AccordionGrupos possuiGrupos={possuiGrupos} />
          <AccordionUsuariosManuais
            possuiUsuariosManuais={possuiUsuariosManuais}
          />
        </Card>
        <div className='pb-deca'>
          <Button
            className='flex items-center justify-center gap-2'
            type='submit'
            disabled={
              (!possuiPerfil &&
                !possuiUsuariosManuais &&
                !possuiCargos &&
                !possuiSetores &&
                !possuiGrupos &&
                !possuiDepartamentos) ||
              isSubmitting
            }
            onClick={handleSubmit(submitGroup)}
          >
            {isSubmitting ? <LoadingButton className='w-4 h-4' /> : null}
            <Trans>Salvar</Trans>
          </Button>
        </div>
      </div>
      {isVisualizarDestinatariosOpen &&
        createPortal(
          <UsuarioDialogByGruposUsuarios
            values={values}
            isDestinatariosOpen={isVisualizarDestinatariosOpen}
            handleCloseDestinatarios={handleCloseVisualizarDestinatarios}
            selection={false}
            isEdit
          />,
          document.body
        )}
    </FormProvider>
  )
}

export default GrupoUsuariosEdicao
